import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

import { fetchWordPressFieldCheckSettings, saveWordPressFieldCheckTargetRules } from './service';
import {
  WORDPRESS_FIELD_CHECK_NO_SECTION_LABEL,
  getWordPressFieldCheckLevelLabel,
  getWordPressFieldCheckSourceHint,
  getWordPressFieldCheckSourceLabel,
} from './fieldCheckUi';
import type {
  WordPressFieldCheckCatalogField,
  WordPressFieldCheckGroupRule,
  WordPressFieldCheckLevel,
  WordPressFieldCheckSettingsDetail,
  WordPressFieldCheckTargetRules,
  WordPressPublicationType,
} from './types';

const TARGETS: Array<{ value: WordPressPublicationType; label: string }> = [
  { value: 'dvh', label: 'DVH' },
  { value: 'foodservice', label: 'Food Service' },
  { value: 'wine', label: 'Vin' },
  { value: 'recipe', label: 'Recept' },
];

type FieldLevelChoice = 'none' | WordPressFieldCheckLevel;

type TargetDraft = {
  fieldLevels: Record<string, WordPressFieldCheckLevel>;
  groups: WordPressFieldCheckGroupRule[];
  removedNotices: string[];
  dirty: boolean;
};

type GroupEditorState = {
  id: string;
  label: string;
  level: WordPressFieldCheckLevel;
  fieldIds: string[];
  isNew: boolean;
};

function buildDraft(
  rules: WordPressFieldCheckTargetRules | undefined,
  catalog: WordPressFieldCheckCatalogField[],
): TargetDraft {
  const catalogById = new Map(catalog.map(field => [field.id, field]));
  const fieldLevels: Record<string, WordPressFieldCheckLevel> = {};
  const removedNotices: string[] = [];

  (rules?.fields || []).forEach((rule) => {
    const field = catalogById.get(rule.fieldId);
    if (!field) {
      removedNotices.push(`Fältet ${rule.fieldName || rule.fieldId} finns inte längre i mallen.`);
      return;
    }
    if (!field.allowedLevels.includes(rule.level)) {
      if (field.allowedLevels.includes('warning')) {
        fieldLevels[field.id] = 'warning';
        removedNotices.push(`${field.label} kan bara ha nivån varning och har ändrats till varning.`);
      } else {
        removedNotices.push(`${field.label} kan inte kontrolleras ensamt och tas bort.`);
      }
      return;
    }
    fieldLevels[field.id] = rule.level;
  });

  const groups: WordPressFieldCheckGroupRule[] = [];
  (rules?.groups || []).forEach((group) => {
    const fieldIds = group.fieldIds.filter(fieldId => catalogById.has(fieldId));
    if (fieldIds.length < 2) {
      removedNotices.push(`Gruppen ${group.label} har färre än två fält kvar i mallen och tas bort.`);
      return;
    }
    if (fieldIds.length !== group.fieldIds.length) {
      removedNotices.push(`Gruppen ${group.label} innehöll fält som inte längre finns i mallen.`);
    }
    groups.push({ ...group, fieldIds });
  });

  return { fieldLevels, groups, removedNotices, dirty: removedNotices.length > 0 };
}

function draftToRules(draft: TargetDraft, catalog: WordPressFieldCheckCatalogField[]): WordPressFieldCheckTargetRules {
  return {
    fields: catalog
      .filter(field => draft.fieldLevels[field.id])
      .map(field => ({ fieldId: field.id, fieldName: field.name, level: draft.fieldLevels[field.id] })),
    groups: draft.groups.map(group => ({ ...group })),
  };
}

function groupCatalogBySection(fields: WordPressFieldCheckCatalogField[]) {
  const sections: Array<{ section: string; fields: WordPressFieldCheckCatalogField[] }> = [];
  fields.forEach((field) => {
    const section = field.section || WORDPRESS_FIELD_CHECK_NO_SECTION_LABEL;
    const last = sections[sections.length - 1];
    if (last && last.section === section) {
      last.fields.push(field);
    } else {
      sections.push({ section, fields: [field] });
    }
  });
  return sections;
}

function getCheckboxHint(field: WordPressFieldCheckCatalogField) {
  return field.allowedLevels.length === 0 ? 'Kryssruta: kontrollera via en grupp ("minst ett av").' : '';
}

export default function FieldCheckSettingsDialog(props: {
  open: boolean;
  initialTarget: WordPressPublicationType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { open, initialTarget, onClose, onSaved } = props;
  const [detail, setDetail] = useState<WordPressFieldCheckSettingsDetail | null>(null);
  const [drafts, setDrafts] = useState<Partial<Record<WordPressPublicationType, TargetDraft>>>({});
  const [target, setTarget] = useState<WordPressPublicationType>(initialTarget || 'dvh');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlyMarked, setOnlyMarked] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [groupEditor, setGroupEditor] = useState<GroupEditorState | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const applyDetail = useCallback((nextDetail: WordPressFieldCheckSettingsDetail, targets: WordPressPublicationType[]) => {
    setDetail(nextDetail);
    setDrafts((previous) => {
      const next = { ...previous };
      targets.forEach((entry) => {
        next[entry] = buildDraft(nextDetail.settings.targets[entry], nextDetail.catalogs[entry] || []);
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setTarget(initialTarget || 'dvh');
    setSearch('');
    setOnlyMarked(false);
    setGroupEditor(null);
    setConfirmClear(false);
    setConfirmClose(false);
    setError(null);
    setSavedMessage(null);
    setLoading(true);
    fetchWordPressFieldCheckSettings()
      .then((nextDetail) => {
        if (cancelled) return;
        setDrafts({});
        applyDetail(nextDetail, TARGETS.map(entry => entry.value));
      })
      .catch((loadError: any) => {
        if (!cancelled) setError(loadError?.response?.data?.message || loadError?.message || 'Kunde inte läsa fältkontrollen.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, initialTarget, applyDetail]);

  const catalog = useMemo(() => detail?.catalogs?.[target] || [], [detail, target]);
  const catalogById = useMemo(() => new Map(catalog.map(field => [field.id, field])), [catalog]);
  const draft = drafts[target];
  const dirtyTargets = TARGETS.filter(entry => drafts[entry.value]?.dirty).map(entry => entry.value);

  const updateDraft = useCallback((updater: (current: TargetDraft) => TargetDraft) => {
    setSavedMessage(null);
    setDrafts((previous) => {
      const current = previous[target];
      if (!current) return previous;
      return { ...previous, [target]: { ...updater(current), dirty: true } };
    });
  }, [target]);

  const setFieldLevel = useCallback((fieldId: string, choice: FieldLevelChoice) => {
    updateDraft((current) => {
      const fieldLevels = { ...current.fieldLevels };
      if (choice === 'none') {
        delete fieldLevels[fieldId];
      } else {
        fieldLevels[fieldId] = choice;
      }
      return { ...current, fieldLevels };
    });
  }, [updateDraft]);

  const groupedFieldIds = useMemo(
    () => new Set((draft?.groups || []).flatMap(group => group.fieldIds)),
    [draft],
  );

  const visibleSections = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = catalog.filter((field) => {
      if (onlyMarked && !draft?.fieldLevels[field.id] && !groupedFieldIds.has(field.id)) return false;
      if (!query) return true;
      return field.label.toLowerCase().includes(query)
        || field.name.toLowerCase().includes(query)
        || String(field.section || '').toLowerCase().includes(query);
    });
    return groupCatalogBySection(filtered);
  }, [catalog, draft, groupedFieldIds, onlyMarked, search]);

  const sectionOptions = useMemo(() => groupCatalogBySection(catalog), [catalog]);

  const warningCount = Object.values(draft?.fieldLevels || {}).filter(level => level === 'warning').length;
  const requiredCount = Object.values(draft?.fieldLevels || {}).filter(level => level === 'required').length;

  const toggleSection = (section: string) => {
    setCollapsedSections((previous) => {
      const next = new Set(previous);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const handleSave = async () => {
    if (!detail || dirtyTargets.length === 0) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      let latest = detail;
      for (const entry of dirtyTargets) {
        const entryDraft = drafts[entry];
        if (!entryDraft) continue;
        latest = await saveWordPressFieldCheckTargetRules(entry, draftToRules(entryDraft, detail.catalogs[entry] || []));
      }
      applyDetail(latest, dirtyTargets);
      setSavedMessage('Fältkontrollen är sparad.');
      onSaved();
    } catch (saveError: any) {
      setError(saveError?.response?.data?.message || saveError?.message || 'Kunde inte spara fältkontrollen.');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (saving) return;
    if (dirtyTargets.length > 0 && !confirmClose) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const saveGroupEditor = () => {
    if (!groupEditor) return;
    const label = groupEditor.label.trim();
    if (!label || groupEditor.fieldIds.length < 2) return;
    updateDraft((current) => {
      const group: WordPressFieldCheckGroupRule = {
        id: groupEditor.id,
        label,
        level: groupEditor.level,
        fieldIds: groupEditor.fieldIds,
      };
      const groups = groupEditor.isNew
        ? [...current.groups, group]
        : current.groups.map(entry => (entry.id === group.id ? group : entry));
      return { ...current, groups };
    });
    setGroupEditor(null);
  };

  const groupEditorFields = (groupEditor?.fieldIds || [])
    .map(fieldId => catalogById.get(fieldId))
    .filter((field): field is WordPressFieldCheckCatalogField => !!field);
  const groupEditorCanBeRequired = groupEditorFields.some(field => field.source === 'athena');

  const renderFieldRow = (field: WordPressFieldCheckCatalogField) => {
    const level = draft?.fieldLevels[field.id];
    const sourceLabel = getWordPressFieldCheckSourceLabel(field.source);
    const checkboxHint = getCheckboxHint(field);
    return (
      <Box
        key={field.id}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'center',
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          bgcolor: level === 'required' ? 'rgba(211, 47, 47, 0.05)' : level === 'warning' ? 'rgba(237, 108, 2, 0.05)' : 'transparent',
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {field.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">{field.type}</Typography>
          {sourceLabel && (
            <Tooltip title={getWordPressFieldCheckSourceHint(field.source) || ''}>
              <Chip size="small" label={sourceLabel} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
            </Tooltip>
          )}
          {groupedFieldIds.has(field.id) && (
            <Chip size="small" label="I grupp" variant="outlined" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
          )}
        </Stack>
        {checkboxHint ? (
          <Typography variant="caption" color="text.secondary">{checkboxHint}</Typography>
        ) : (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={level || 'none'}
            onChange={(_event, nextValue: FieldLevelChoice | null) => {
              if (nextValue) setFieldLevel(field.id, nextValue);
            }}
            sx={{ '& .MuiToggleButton-root': { py: 0.15, px: 1, textTransform: 'none', fontSize: '0.75rem' } }}
          >
            <ToggleButton value="none">Ingen</ToggleButton>
            <ToggleButton value="warning" color="warning">Varning</ToggleButton>
            <ToggleButton value="required" color="error" disabled={!field.allowedLevels.includes('required')}>
              Obligatorisk
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleRequestClose} maxWidth="lg" fullWidth>
      <DialogTitle>Fältkontroll inför WordPress-publicering</DialogTitle>
      <DialogContent dividers sx={{ minHeight: 520 }}>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Välj vilka fält som ska kontrolleras innan en post publiceras. <strong>Varning</strong> markerar posten men
            stoppar inte publicering. <strong>Obligatorisk</strong> stoppar publicering tills fältet är ifyllt. Fält utan
            markering kontrolleras inte.
          </Typography>

          <Tabs
            value={target}
            onChange={(_event, nextValue: WordPressPublicationType) => {
              setTarget(nextValue);
              setGroupEditor(null);
              setConfirmClear(false);
            }}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}
          >
            {TARGETS.map(entry => (
              <Tab
                key={entry.value}
                value={entry.value}
                label={drafts[entry.value]?.dirty ? (
                  <Badge variant="dot" color="warning"><Box component="span" sx={{ pr: 0.75 }}>{entry.label}</Box></Badge>
                ) : entry.label}
              />
            ))}
          </Tabs>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {savedMessage && <Alert severity="success" onClose={() => setSavedMessage(null)}>{savedMessage}</Alert>}
          {confirmClose && (
            <Alert
              severity="warning"
              action={(
                <Stack direction="row" spacing={1}>
                  <Button size="small" color="inherit" onClick={() => setConfirmClose(false)}>Fortsätt redigera</Button>
                  <Button size="small" color="inherit" onClick={onClose}>Stäng utan att spara</Button>
                </Stack>
              )}
            >
              Du har osparade ändringar.
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && detail && draft && catalog.length === 0 && (
            <Alert severity="warning">Ingen mall med fält hittades för den här fliken.</Alert>
          )}

          {!loading && detail && draft && catalog.length > 0 && (
            <>
              {draft.removedNotices.length > 0 && (
                <Alert severity="info">
                  Mallen har ändrats sedan reglerna sparades. Följande justeras när du sparar:
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {draft.removedNotices.map(notice => <li key={notice}>{notice}</li>)}
                  </Box>
                </Alert>
              )}

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Sök fält eller sektion"
                  sx={{ flex: '1 1 260px' }}
                  InputProps={{ startAdornment: <SearchIcon fontSize="small" style={{ marginRight: 8, opacity: 0.65 }} /> }}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={onlyMarked} onChange={event => setOnlyMarked(event.target.checked)} />}
                  label={<Typography variant="body2">Visa bara markerade</Typography>}
                />
                <Chip size="small" color="warning" variant="outlined" label={`${warningCount} varning${warningCount === 1 ? '' : 'ar'}`} />
                <Chip size="small" color="error" variant="outlined" label={`${requiredCount} obligatorisk${requiredCount === 1 ? '' : 'a'}`} />
                <Chip size="small" color="info" variant="outlined" label={`${draft.groups.length} grupp${draft.groups.length === 1 ? '' : 'er'}`} />
                <Box sx={{ flex: 1 }} />
                {confirmClear ? (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2">Rensa alla markeringar och grupper?</Typography>
                    <Button size="small" onClick={() => setConfirmClear(false)}>Nej</Button>
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      onClick={() => {
                        updateDraft(current => ({ ...current, fieldLevels: {}, groups: [] }));
                        setConfirmClear(false);
                      }}
                    >
                      Ja, rensa
                    </Button>
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setConfirmClear(true)}
                    disabled={Object.keys(draft.fieldLevels).length === 0 && draft.groups.length === 0}
                  >
                    Rensa alla markeringar
                  </Button>
                )}
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2">Grupper – minst ett av</Typography>
                    <Typography variant="caption" color="text.secondary">
                      En grupp räknas som ifylld när minst ett av fälten har värde, till exempel Ingrediens 1–28.
                    </Typography>
                  </Box>
                  {!groupEditor && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setGroupEditor({ id: `group-${Date.now()}`, label: '', level: 'warning', fieldIds: [], isNew: true })}
                    >
                      Ny grupp
                    </Button>
                  )}
                </Box>

                <Stack spacing={0.75}>
                  {draft.groups.length === 0 && !groupEditor && (
                    <Typography variant="body2" color="text.secondary">Inga grupper.</Typography>
                  )}
                  {draft.groups.map(group => (
                    groupEditor?.id === group.id ? null : (
                      <Box key={group.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          color={group.level === 'required' ? 'error' : 'warning'}
                          variant="outlined"
                          label={getWordPressFieldCheckLevelLabel(group.level)}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{group.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
                          {group.fieldIds.map(fieldId => catalogById.get(fieldId)?.label || fieldId).join(', ')}
                        </Typography>
                        <IconButton
                          size="small"
                          title="Redigera grupp"
                          onClick={() => setGroupEditor({ ...group, isNew: false })}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Ta bort grupp"
                          onClick={() => updateDraft(current => ({ ...current, groups: current.groups.filter(entry => entry.id !== group.id) }))}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                  ))}

                  {groupEditor && (
                    <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'grey.50' }}>
                      <Stack spacing={1.25}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                          <TextField
                            size="small"
                            label="Gruppnamn"
                            value={groupEditor.label}
                            onChange={event => setGroupEditor(current => (current ? { ...current, label: event.target.value } : current))}
                            sx={{ flex: 1 }}
                            autoFocus
                          />
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={groupEditor.level}
                            onChange={(_event, nextValue: WordPressFieldCheckLevel | null) => {
                              if (nextValue) setGroupEditor(current => (current ? { ...current, level: nextValue } : current));
                            }}
                            sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
                          >
                            <ToggleButton value="warning" color="warning">Varning</ToggleButton>
                            <ToggleButton value="required" color="error" disabled={!groupEditorCanBeRequired && groupEditorFields.length > 0}>
                              Obligatorisk
                            </ToggleButton>
                          </ToggleButtonGroup>
                          <Select
                            size="small"
                            displayEmpty
                            value=""
                            onChange={(event) => {
                              const section = String(event.target.value || '');
                              const sectionFields = sectionOptions.find(entry => entry.section === section)?.fields || [];
                              setGroupEditor(current => (current ? {
                                ...current,
                                label: current.label || section,
                                fieldIds: Array.from(new Set([...current.fieldIds, ...sectionFields.map(field => field.id)])),
                              } : current));
                            }}
                            sx={{ minWidth: 220 }}
                          >
                            <MenuItem value="" disabled>Lägg till alla i sektion…</MenuItem>
                            {sectionOptions.map(entry => (
                              <MenuItem key={entry.section} value={entry.section}>
                                {entry.section} ({entry.fields.length})
                              </MenuItem>
                            ))}
                          </Select>
                        </Stack>
                        <Autocomplete
                          multiple
                          size="small"
                          options={catalog}
                          groupBy={option => option.section || WORDPRESS_FIELD_CHECK_NO_SECTION_LABEL}
                          getOptionLabel={option => option.label}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={groupEditorFields}
                          onChange={(_event, nextValue) => setGroupEditor(current => (current ? {
                            ...current,
                            fieldIds: nextValue.map(field => field.id),
                          } : current))}
                          renderInput={params => <TextField {...params} label="Fält i gruppen" placeholder="Välj fält" />}
                        />
                        {!groupEditorCanBeRequired && groupEditorFields.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Gruppen innehåller bara Dabas- eller skrivskyddade fält och kan bara ge varning.
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => setGroupEditor(null)}>Avbryt</Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={saveGroupEditor}
                            disabled={!groupEditor.label.trim() || groupEditor.fieldIds.length < 2}
                          >
                            {groupEditor.isNew ? 'Lägg till grupp' : 'Uppdatera grupp'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1 }}>
                {visibleSections.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Inga fält matchar.</Typography>
                )}
                {visibleSections.map((section, index) => {
                  const forceOpen = search.trim().length > 0 || onlyMarked;
                  const collapsed = !forceOpen && collapsedSections.has(section.section);
                  const markedCount = section.fields.filter(field => draft.fieldLevels[field.id]).length;
                  return (
                    <Box key={`${section.section}-${index}`}>
                      {index > 0 && <Divider sx={{ my: 0.5 }} />}
                      <Box
                        onClick={() => toggleSection(section.section)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        <Typography variant="subtitle2">{section.section}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {section.fields.length} fält{markedCount > 0 ? ` · ${markedCount} markerade` : ''}
                        </Typography>
                      </Box>
                      {!collapsed && section.fields.map(renderFieldRow)}
                    </Box>
                  );
                })}
              </Paper>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
          {dirtyTargets.length > 0
            ? `Osparade ändringar: ${TARGETS.filter(entry => dirtyTargets.includes(entry.value)).map(entry => entry.label).join(', ')}`
            : detail?.settings.updatedAt
              ? `Senast sparad ${new Date(detail.settings.updatedAt).toLocaleString('sv-SE')}`
              : ''}
        </Typography>
        <Button onClick={handleRequestClose} disabled={saving}>Stäng</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading || dirtyTargets.length === 0}>
          {saving ? 'Sparar...' : 'Spara'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
